import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core'
import { GeneralCampaignSettingsModel } from '@models/generalCampaingSettings.model';
import { FormGroup, FormBuilder, AbstractControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ColorEvent } from 'ngx-color';
import { TimeZoneModel } from '@models/timeZone.model';
import { onClickToReviewFeedback, onOpenEmailSMS } from '@utilities/data-utils'
import { Days } from '../../../core/enums/days.enum'
import deepEqual from 'deep-equal'
import { combineLatestWith, map, Observable, Subscription } from 'rxjs';
import { WorkflowModel } from '@models/workflow.model';
import { SentimentCheck } from '@enums/sentimentChek.enum';
import { SentimentCheckSetting } from '@enums/sentimentCheckSettings.enum';
import Swal from 'sweetalert2';
import { UpgradeFeatureModalComponent } from 'src/app/shared/components/upgrade-feature-modal/upgrade-feature-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UpgradeAction } from '@models/upgradePlan.model';
import { LocationsFacadeService } from '@services/locations/locations-facade.service';

@Component({
  selector: 'app-general-settings-form',
  templateUrl: './general-settings-form.component.html',
  styleUrls: ['./general-settings-form.component.sass']
})
export class GeneralSettingsFormComponent implements OnInit, OnDestroy {
  @Input() generalSettingsModelInput: GeneralCampaignSettingsModel;
  @Input() generalSettingsModelInput$: Observable<GeneralCampaignSettingsModel>;
  @Input() allowedQuota: number;
  @Input() disabled: boolean = false;
  @Output() onSave = new EventEmitter<GeneralCampaignSettingsModel>();
  @Input() workFlow: WorkflowModel;
  @Input() workFlow$: Observable<WorkflowModel>;
  @Input() isLocationSentimentCheckEnabled: boolean = false;
  @Input() isEnabledUpgradeModal: boolean = false;

  generalSettingsForm: FormGroup;
  generalSettingsFormInitialValue: any;

  startTime: AbstractControl;
  endTime: AbstractControl;
  numberofReminders: any[];
  timeZones: TimeZoneModel[];
  timeZonesUSA: any[];
  filteredTimeZones: TimeZoneModel[];
  days = Days;
  _subs: Subscription = new Subscription();

  stopCampaignRemindersOptions$ = this.translate.get("general-campaign-form.onOpenLabel").pipe(
    combineLatestWith(this.translate.get("general-campaign-form.onClcikLabel"))
  ).pipe(
    map(([onOpenLabel, onClickLabel]) => ([
      { value: onOpenEmailSMS, label: onOpenLabel },
      { value: onClickToReviewFeedback, label: onClickLabel },
    ]))
  );

  undoDisable: boolean = true;
  saveDisable: boolean = true;
  sentimentCheck = SentimentCheck;
  sentimentCheckSettings = SentimentCheckSetting;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private window: Window,
    private modalService: NgbModal,
    private locationsFacadeService: LocationsFacadeService,
  ) {
    this.numberofReminders = [
      { label: '0', value: 0 },
      { label: '1', value: 1 },
      { label: '2', value: 2 },
    ];
    this.timeZones = this.window.moment.tz.names().map(name => ({
      name: name,
      abbr: this.window.moment.tz(name).zoneAbbr()
    }));
    console.log('timeZones:', this.timeZones);
    this.filteredTimeZones = this.timeZones;
    this.timeZonesUSA = [
      { label: '(EST)', value: '(EST)' },
      { label: '(CST)', value: '(CST)' },
      { label: '(MST)', value: '(MST)' },
      { label: '(PST)', value: '(PST)' },
      { label: 'Other', value: 'other' }
    ]
  }

  ngOnInit() {
    this.generalSettingsForm = this.fb.group({
      name: [this.generalSettingsModelInput.name, [Validators.required, this.ValidateCampaignName]],
      senderName: [this.generalSettingsModelInput.senderName, [Validators.required, this.ValidateSenderName]],
      quota: [this.generalSettingsModelInput.quota ? this.generalSettingsModelInput.quota : 0],
      methods: [this.generalSettingsModelInput.methods, [this.ValidateMethods]],
      color: [this.generalSettingsModelInput.color ? this.generalSettingsModelInput.color : '#FFFFFF'],
      stopCampaign: [this.generalSettingsModelInput.stopCampaign],
      daysForDuplicates: [this.generalSettingsModelInput.daysForDuplicates ? this.generalSettingsModelInput.daysForDuplicates : 180],
      delayEmail: [this.generalSettingsModelInput.delayEmail],
      delaySMS: [this.generalSettingsModelInput.delaySMS],
      schedule: this.fb.group({
        days: [([...this.generalSettingsModelInput.schedule.days]), this.ValidateDays],
        startTime: [this.generalSettingsModelInput.schedule.startTime],
        endTime: [this.generalSettingsModelInput.schedule.endTime],
        timeZone: [this.generalSettingsModelInput.schedule.timeZone, this.ValidateTimeZone]
      }),
      enablePrivateFeedback: [this.generalSettingsModelInput.enablePrivateFeedback, [Validators.required]],
      enableSentimentCheck: [this.workFlow.enableSentimentCheck, Validators.required],
      sentimentCheckType: this.workFlow.sentimentCheckType,
      sentimentCheckSettings: this.workFlow.sentimentCheckSettings,
    }, {
      validators: (formControl) => (formControl.value.name ? !formControl.value.name.trim().length : true)
        || (!formControl.value.quota)
        || (formControl.value.quota > this.allowedQuota)
        || ((formControl.value.daysForDuplicates < 1))
        || (formControl.value.senderName ? !formControl.value.senderName.trim().length : true)
        || (formControl.value.methods.length ? false : true)
        || (formControl.value.delayEmail ? false : true)
        || (formControl.value.delaySMS ? false : true)
        || (formControl.value.schedule.days.length ? false : true)
        || (formControl.value.schedule.startTime ? false : true)
        || (formControl.value.schedule.endTime ? false : true)
        || (formControl.value.schedule.timeZone.name ? false : true)
        ? { invalid: true } : null
    })

    this.generalSettingsFormInitialValue = this.generalSettingsForm.value;

    this.generalSettingsForm.valueChanges.subscribe(_ => {
      this.allowUndo();
      this.allowSave();
      this.validateEndTime();
    })

    this.startTime = (this.generalSettingsForm.controls['schedule'] as FormGroup).controls['startTime'];
    this.endTime = (this.generalSettingsForm.controls['schedule'] as FormGroup).controls['endTime'];

    this.generalSettingsModelInput$.subscribe(item => {
      this.generalSettingsModelInput = item;
      this.generalSettingsForm.patchValue({
        name: item.name,
        senderName: item.senderName,
        quota: item.quota ? item.quota : 0,
        methods: item.methods,
        color: item.color ? item.color : '#FFFFFF',
        stopCampaign: item.stopCampaign,
        daysForDuplicates: item.daysForDuplicates ? item.daysForDuplicates : 180,
        delayEmail: item.delayEmail,
        delaySMS: item.delaySMS,
        schedule: {
          days: [...item.schedule.days],
          startTime: item.schedule.startTime,
          endTime: item.schedule.endTime,
          timeZone: item.schedule.timeZone,
        },
        enablePrivateFeedback: item.enablePrivateFeedback,
      })
    })

    this.workFlow$.subscribe(item => {
      this.workFlow = item;
      this.generalSettingsForm.patchValue({
        enableSentimentCheck: item.enableSentimentCheck,
        sentimentCheckType: item.sentimentCheckType,
        sentimentCheckSettings: item.sentimentCheckSettings,
      })
    })
  }

  changeMethods() {
    this.generalSettingsForm.patchValue({
      methods: this.generalSettingsForm.value.methods.sort()
    })
  }

  changeSwitch() {
    this.generalSettingsForm.patchValue({
      enablePrivateFeedback: this.generalSettingsForm.value.enablePrivateFeedback
    })
  }

  moveDays(day: number, daysSchedule: number[]) {
    daysSchedule.push(day)
    return daysSchedule.sort((a, b) => a - b)
  }

  changeTimeZonesList(event) {
    this.timeZones = this.window.moment.tz.names().map((name => ({
      name: name,
      abbr: "(" + this.window.moment.tz(name).zoneAbbr() + ")"
    })));

    this.timeZones = event.value.value == 'other'
      ? this.timeZones.filter((timezone) => !timezone.abbr.includes('(EST)') && !timezone.abbr.includes('(CST)') && !timezone.abbr.includes('(MST)') && !timezone.abbr.includes('(PST)'))
      : this.timeZones.filter((timezone) => timezone.abbr.includes(event.value.value))

    this.timeZones = this.timeZones.sort((a, b) => a.name.localeCompare(b.name))

    this.generalSettingsForm.patchValue({
      schedule: {
        ...this.generalSettingsForm.value.schedule,
        timeZone: {
          ...this.generalSettingsForm.value.schedule.timeZone,
          name: ''
        }
      }
    })
  }

  search(event) {
    this.filteredTimeZones = this.timeZones.filter((timezone) => timezone.name.toLowerCase().includes(event.query.toLowerCase()))
  }

  changeColorComplete(hex: string) {
    // start at 1 to skip #
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    const rgbString = `${r},${g},${b}`;
    console.log('[general-settings-form changeColorComplete]', 'rgbString', rgbString);

    this.generalSettingsForm.patchValue({
      color: rgbString,
    });
  }

  allowUndo() {
    this.undoDisable = deepEqual(this.generalSettingsModelInput, this.generalSettingsForm.value);
    //console.log('undoDisable:', this.undoDisable);
  }

  allowSave() {
    this.saveDisable = !this.generalSettingsForm.valid || deepEqual(this.generalSettingsModelInput, this.generalSettingsForm.value) || this.validateEndTime();
  }

  undo() {
    this.generalSettingsForm.reset({
      name: this.generalSettingsModelInput.name,
      senderName: this.generalSettingsModelInput.senderName,
      quota: this.generalSettingsModelInput.quota ? this.generalSettingsModelInput.quota : 0,
      methods: this.generalSettingsModelInput.methods,
      color: this.generalSettingsModelInput.color ? this.generalSettingsModelInput.color : '#FFFFFF',
      stopCampaign: this.generalSettingsModelInput.stopCampaign,
      daysForDuplicates: this.generalSettingsModelInput.daysForDuplicates ? this.generalSettingsModelInput.daysForDuplicates : 180,
      delayEmail: this.generalSettingsModelInput.delayEmail,
      delaySMS: this.generalSettingsModelInput.delaySMS,
      schedule: {
        days: [...this.generalSettingsModelInput.schedule.days],
        startTime: this.generalSettingsModelInput.schedule.startTime,
        endTime: this.generalSettingsModelInput.schedule.endTime,
        timeZone: this.generalSettingsModelInput.schedule.timeZone,
      },
      enablePrivateFeedback: this.generalSettingsModelInput.enablePrivateFeedback,
      enableSentimentCheck: this.workFlow.enableSentimentCheck,
      sentimentCheckType: this.workFlow.sentimentCheckType,
      sentimentCheckSettings: this.workFlow.sentimentCheckSettings,
    });
  }

  ValidateCampaignName(control: AbstractControl) {
    if (control.value && control.value.trim().length) {
      return null;
    }
    return { invalid: true };
  }

  ValidateSenderName(control: AbstractControl) {
    if (control.value && control.value.trim().length) {
      return null;
    }
    return { invalid: true };
  }

  ValidateMethods(control: AbstractControl) {
    if (control.value && control.value.length) {
      return null;
    }
    return { invalid: true };
  }

  ValidateDays(control: AbstractControl) {
    if (control.value && control.value.length) {
      return null;
    }
    return { invalid: true };
  }

  validateEndTime(): boolean {
    if ((this.endTime.value.hour === this.startTime.value.hour)
      && (this.endTime.value.minute < this.startTime.value.minute)
      || (this.endTime.value.hour < this.startTime.value.hour)) {
      return true
    }
  }

  ValidateTimeZone(control: AbstractControl) {
    if (control.value && control.value.name) {
      if (control.value.name.trim().length)
        return null;
      else
        return { invalid: true };
    }
    return { invalid: true };
  }

  changeSwitchSentimentCheck() {
    this.generalSettingsForm.patchValue({
      sentimentCheckType: SentimentCheck.Thumbs,
      sentimentCheckSettings: null,
    })
  }

  sentimentCheckChanged(sentimentCheckType: string) {
    this.generalSettingsForm.patchValue({
      sentimentCheckType: sentimentCheckType,
      sentimentCheckSettings: sentimentCheckType == this.sentimentCheck.Thumbs
        ? null
        : sentimentCheckType == this.sentimentCheck.Faces
          ? this.sentimentCheckSettings.neutralReview
          : this.sentimentCheckSettings.threeForReview,
    })
  }
  showSentimentCheckInfo() {
    Swal.fire({
      title: 'Upgrade to Add Sentiment Check',
      text: 'To add sentiment check feature to your plan, contact Support',
      icon: 'warning',
      showCancelButton: false,
      confirmButtonText: 'Ok!'
    })
  }
  openNewModalFeature() {
    const modalRef = this.modalService.open(UpgradeFeatureModalComponent, { centered: true, backdrop: 'static', keyboard: false });
    modalRef.componentInstance.title = "Upgrade to Add Sentiment Check";
    modalRef.componentInstance.bodyText = "Learn more about this feature";
    modalRef.componentInstance.bodyTextLink = "https://help.getmorereviews.com/en/articles/4351973-what-is-the-sentiment-check-workflow";
    //subscribe to child output event
    this._subs.add(
      modalRef.componentInstance.addOnEmit
        .subscribe(val => {
          this.locationsFacadeService.upgradePlan({ action: UpgradeAction.SENTIMENT_CHECK, cancelFeatureButton: "" })
        })
    )
    this._subs.add(
      modalRef.componentInstance.upgradePlanEmit
        .subscribe(val => {
          this.locationsFacadeService.upgradePlan({ action: UpgradeAction.UPGRADE_ALL, cancelFeatureButton: "" })
        })
    )
    this._subs.add(
      modalRef.componentInstance.cancelEmit
        .subscribe(val => {
          this.locationsFacadeService.upgradePlan({ action: UpgradeAction.CANCEL, cancelFeatureButton: "Sentiment Check" })
        })
    )
  }

  handleSentimentChange(e: any) {
    if (!this.isLocationSentimentCheckEnabled) {
      if (this.isEnabledUpgradeModal) {
        this.openNewModalFeature()
      }
      else {
        this.showSentimentCheckInfo()
      }
      this.generalSettingsForm.patchValue({
        enableSentimentCheck: false,
      })
    } else {
      this.changeSwitchSentimentCheck()
      if (e.checked == false) {
        this.locationsFacadeService.upgradePlan({ action: UpgradeAction.DISABLED_SENTIMENT_CHECK, cancelFeatureButton: "" })
      }
    }
  }
  ngOnDestroy(): void {
    if (this._subs) this._subs.unsubscribe()
  }
}
